export const MATING_MOTIFS_CHAPTER = {
  id: "mating_motifs_ch1",
  title: "Chapter 1: Mating Motifs",
  description: "Learn typical mating motifs including open lines, Anastasia's mate, Arabian mate, battery attacks, and Boden's mate.",
  pages: [
    {
      type: "theory",
      title: "Mate Along Open Lines - Introduction",
      motif: "Open Lines",
      text: "This is a very well-known and important motif. It involves two major pieces (rook + queen or two rooks) operating along open files or ranks and forcing the opposing king to the edge of the board. Since it has no way out, the king is mated there.",
      fen: "6k1/8/p7/r1r2Pp1/3R2Pp/1p6/1P4P1/3R2K1 w - - 0 35",
      exampleTitle: "Em. Lasker - Meyer (1900)",
      exampleText: "White finds a forced mate here using the open files:\n1. Rd8+ Kf7\n2. R1d7+ Kf6 (2...Kh6 3. Rh8#)\n3. Rf8+ Ke5\n4. Re8+ Kf4 (4...Kf6 5. Re6#)\n5. Rd4+ Kg3\n6. Re3#",
      moves: ["Rd8+", "Kf7", "R1d7+", "Kf6", "Rf8+", "Ke5", "Re8+", "Kf4", "Rd4+", "Kg3", "Re3#"]
    },
    {
      type: "theory",
      title: "Mate Along Open Lines - Example 2",
      motif: "Open Lines",
      text: "Another example of this theme shows how control of the open e-file coordinates with the active queen to force resignation.",
      fen: "r2r2k1/pp5p/1p2pQ2/n1qP4/2P5/8/PP3PPP/3RR1K1 w - - 0 28",
      exampleTitle: "A. Yusupov - P. Schlosser (1997)",
      exampleText: "White plays 1. Re5! threatening Rg5+ and mate. Black resigned due to:\n- 1...Qd7 2. Rg5+ Kf7/Kh7 3. Qg6+ Kh8 4. Rh5+ Qh7 5. Rxh7#\n- 1...Qf8 2. Rg5+ Kh8 3. Rh5#\n- 1...Qc7 2. Rg5+ Kh7 3. Qg6+ Kh8 4. Rh5+ Qh7 5. Rxh7#",
      moves: ["Re5"]
    },
    {
      type: "theory",
      title: "Anastasia's Mate - Introduction",
      motif: "Anastasia's Mate",
      text: "In this spectacular combination, the attacking side uses the combined power of the queen, rook, and knight. The knight occupies the e2-square to block the g1 and g3 escape squares, allowing the rook to mate on the h-file.",
      fen: "1q3rbk/Q5pp/8/3P4/1r3n2/R3N3/P1B2PP1/5RK1 b - - 0 1",
      exampleTitle: "J. Gast - E. Bhend (1987)",
      exampleText: "Black activates the knight and sacrifices the queen to force the mate:\n1... Ne2+!\n2. Kh1 Qxh2+!!\nWhite resigned, because 3. Kxh2 Rh4# is checkmate.",
      moves: ["Ne2+", "Kh1", "Qxh2+", "Kxh2", "Rh4#"]
    },
    {
      type: "theory",
      title: "Anastasia's Mate - Example 2",
      motif: "Anastasia's Mate",
      text: "White's knight on g4 looks dangerous, but Black spots the tactical sequence using the same mating theme.",
      fen: "6k1/p4p1p/3p1p2/2p1r3/2PnrqN1/P6P/1P1Q1PP1/3R1RK1 b - - 0 1",
      exampleTitle: "Kamyshev - Sokolsky (1936)",
      exampleText: "Black plays:\n1... Ne2+ \n2. Kh1 Qxg4!! (Sacrificing the queen to force open the h-file)\n3. hxg4 (3. f3 Qg3+ 4. Kg1 Nf1 5. Rxf1 Rd4-+) \n3... Rh5+!\n4. gxh5 Rh4#",
      moves: ["Ne2+", "Kh1", "Qxg4", "hxg4", "Rh5+", "gxh5", "Rh4#"]
    },
    {
      type: "theory",
      title: "The Arabian Mate",
      motif: "Arabian Mate",
      text: "This is a very old motif in which a knight and a rook deliver mate together. The knight controls the key escape squares and protects the rook on the corner square.",
      fen: "6k1/5pp1/8/5P2/R6p/1P3N1P/3r2nr/6R1 b - - 0 1",
      exampleTitle: "T. Casper - A. Yusupov (1999)",
      exampleText: "Black sacrifices the exchange to coordinate the mate:\n1... Rxf3!! (Prepares discovered check)\n2. gxf3 Ng3+ \n3. Kh1 (3. Rg2 Nxf3+ 4. Kh1 Rd1+ 5. Rg1 Rxg1#)\n3... Nxf3",
      moves: ["Rxf3", "gxf3", "Ng3+", "Kh1", "Nxf3"]
    },
    {
      type: "theory",
      title: "The Queen-Bishop Battery",
      motif: "Queen-Bishop Battery",
      text: "The coordination of the queen and bishop along a single diagonal (a battery) is one of the most powerful attacking structures in chess.",
      fen: "rb2r1k1/1p1b1ppp/p1nq4/3p4/3Nn3/1QB1PN2/PP2BPPP/2R2RK1 b - - 7 17",
      exampleTitle: "M. Gerusel - G. Sosonko (1977)",
      exampleText: "Black plays:\n1... Nxd4!\nWins a piece due to the mating threat on h2: if 2. Bxd4 Nd2! 3. Qd1 Nxf3+ 4. Bxf3 Qxh2#\nSo White played 2. exd4 Nxc3 3. Rxc3 Rxe2 winning a piece.",
      moves: ["Nxd4", "exd4", "Nxc3", "Rxc3", "Rxe2"]
    },
    {
      type: "theory",
      title: "The Queen-Rook Battery",
      motif: "Queen-Rook Battery",
      text: "A battery consisting of a queen and a rook is equally dangerous. Aligning them on an open file creates an unstoppable mating threat.",
      fen: "5rk1/pp1nrrp1/2p3p1/3p4/3P1P1P/1BP5/PP2Q3/3RK2R w K - 0 24",
      exampleTitle: "J. Cazenove - Hull (1818)",
      exampleText: "White aligns the queen and rook on the open h-file:\n1. Qh2! \nThreatening Qh8# which cannot be stopped. Black resigned.",
      moves: ["Qh2"]
    },
    {
      type: "theory",
      title: "Standard Attack Example",
      motif: "Standard Attack",
      text: "Standard attacks often combine multiple tactical ideas to pierce the opponent's defenses, utilizing pin and deflection.",
      fen: "1q2r1k1/1p1r2pp/1bpp1p1B/p1nPpP1Q/P1P1P3/2N4P/1P3P1K/1R4R1 w - - 2 25",
      exampleTitle: "A. Yusupov - J. Rubinetti (1982)",
      exampleText: "White crashes through:\n1. Bxg7! Rxg7\n2. Rxg7+ Kxg7\n3. Rg1+ Kh8\n4. Qf7! \nThreatening both Qg7# and Qf8#. Black cannot defend both.",
      moves: ["Bxg7", "Rxg7", "Rxg7+", "Kxg7", "Rg1+", "Kh8", "Qf7"]
    },
    {
      type: "theory",
      title: "Boden's Mate - Introduction",
      motif: "Boden's Mate",
      text: "Boden's Mate involves two active, crossing bishops attacking a queenside castled king. Typically, it features a queen sacrifice on c3 or c6 to clear the b-file/c-file pawns.",
      fen: "2k1rb1r/ppp3pp/2n1q2/5b2/2B2P2/2P1BQ2/PP1N1P1P/2KR3R b - - 8 13",
      exampleTitle: "Schulder - S. Boden (1853)",
      exampleText: "Black plays:\n1... d5!\n2. Bxd5 Qxd5!! (The queen sacrifice initiates the cross-bishops mate)\n3. Qxd5 Ba3#",
      moves: ["d5", "Bxd5", "Qxd5", "Qxd5", "Ba3#"]
    },
    {
      type: "theory",
      title: "Boden's Mate - Example 2",
      motif: "Boden's Mate",
      text: "White uses the dangerous f4-b8 diagonal and Boden's mating idea to bring the queen into a devastating attack.",
      fen: "2kr1bnr/pp1nqppp/2p1b3/3pP3/3P1B2/2N2NP1/PPP1Q2P/2KR1B1R w - - 0 11",
      exampleTitle: "M. Chalupnik - E. Solozhenkin (1989)",
      exampleText: "White plays:\n1. Qa6!! Bc5 (1... bxa6?? 2. Bxa6# is mate)\n2. Na4! Qe3+\n3. Bxe3 bxa6\n4. Bxa6+ Kb8\n5. Ne5 Kc7\n6. Nxf7! winning.",
      moves: ["Qa6", "Bc5", "Na4", "Qe3+", "Bxe3", "bxa6", "Bxa6+", "Kb8", "Ne5", "Kc7", "Nxf7"]
    },
    {
      type: "exercise",
      title: "Exercise 1: Anastasia's Mate",
      motif: "Anastasia's Mate",
      question: "Deliver Anastasia's Mate. Find the forced checkmate sequence for White.",
      fen: "r4rk1/pp3ppp/8/3N3Q/8/3R4/PPP2PPP/5RK1 w - - 0 1",
      answers: ["Ne7+", "Qxh7+", "Rh3#"],
      hint: "Start with a check to restrict the king to the corner, then prepare a queen sacrifice on the h-file."
    },
    {
      type: "exercise",
      title: "Exercise 2: Arabian Mate Threat",
      motif: "Arabian Mate",
      question: "Find the strong attacking move that forces a mate along the h-file using the Knight and Rook.",
      fen: "6rk/3R3p/8/8/6N1/8/8/6K1 w - - 0 1",
      answers: ["Nf6"],
      hint: "Move the knight to target the h7 square, threatening mate on h7. If Black defends with Rg7, you can win."
    },
    {
      type: "exercise",
      title: "Exercise 3: Boden's Mate Sacrifice",
      motif: "Boden's Mate",
      question: "Deliver Boden's Mate. Sac the Queen to open the king's pawn shield.",
      fen: "2kr1bnr/pp1nqppp/2p1b3/8/2B2B2/5Q2/PPP2PPP/2KR2NR w - - 0 1",
      answers: ["Qxc6+", "Ba6#"],
      hint: "Look for a queen capture on c6. After the pawn recaptures, the c4 bishop can mate on a6."
    }
  ]
};
